import { Type } from 'class-transformer';

import { DayOfWeekEnum } from '../../../shared/enums/day-of-week.enum';
import { DistanceUnitEnum } from '../../../shared/enums/distance-unit.enum';
import { DurationUnitEnum } from '../../../shared/enums/duration-unit.enum';
import { TaxRateTypeEnum } from '../../../shared/enums/tax-rate-type.enum';
import { AppSubjectEnum } from '../../../shared/modules/authz/enums/app-subject.enum';
import { CaslSubject } from '../../../shared/modules/authz/decorators/casl-subject.decorator';
import { BaseDto } from '../../../shared/modules/base/dtos/base.dto';
import { TaxSettingsInterface } from '../../../shared/modules/settings/interfaces/tax-settings.interface';
import { FuelConsumptionUnitEnum } from '../../shared/enums/fuel-consumption-unit.enum';
import { VehicleEmissionsUnitCO2Enum } from '../../vehicles/enums/vehicle-emissions-unit-co2.enum';
import { VehicleEngineOutputUnitEnum } from '../../vehicles/enums/vehicle-engine-output-unit.enum';
import { VehicleFuelTankCapacityUnitEnum } from '../../vehicles/enums/vehicle-fuel-tank-capacity-unit.enum';
import { ReservationCheckoutModeEnum } from '../enums/reservation-checkout-mode.enum';
import { ReservationPaymentAmountEnum } from '../enums/reservation-payment-amount.enum';

import { TenantSettingsDiscountsDto } from './tenant-settings-discounts.dto';
import { TenantSettingsMarketingDto } from './tenant-settings-marketing.dto';
import { TenantSettingsPublicIntegrationsDto } from './tenant-settings-public-integrations.dto';
import { TenantSettingsReportsDto } from './tenant-settings-reports.dto';
import { TenantSettingsReservationPercentageDepositRoundingDto } from './tenant-settings-reservation-percentage-deposit-rounding.dto';

/**
 * Settings DTO
 * @description
 * IMPORTANT: Do not expose API keys, passwords, or payment credentials. Strip those server-side and use
 * is...Configured flags where values are hidden. E-signature sender name and email are non-secret
 * configuration returned for authenticated admin settings and preferences.
 */
@CaslSubject(AppSubjectEnum.SETTINGS)
export class TenantSettingsDto extends BaseDto implements TaxSettingsInterface {
  // Format settings
  shortDateFormat?: string | null;
  shortTimeFormat?: string | null;
  longDateFormat?: string | null;
  longTimeFormat?: string | null;
  firstDayOfWeek?: DayOfWeekEnum | null;
  timezone?: string | null;

  amountDecimalDigits?: number | null;
  amountDecimalSeparator?: string | null;
  amountGroupSeparator?: string | null;

  // Tax settings
  isTaxIncluded?: boolean | null;
  defaultTaxRateType?: TaxRateTypeEnum | null;

  standardTaxRate?: number | null;
  exemptTaxRate?: number | null;
  zeroTaxRate?: number | null;
  reducedTaxRate1?: number | null;
  reducedTaxRate2?: number | null;
  reducedTaxRate3?: number | null;
  reverseChargeTaxRate?: number | null;

  standardTaxRateLabel?: string | null;
  exemptTaxRateLabel?: string | null;
  zeroTaxRateLabel?: string | null;
  reducedTaxRate1Label?: string | null;
  reducedTaxRate2Label?: string | null;
  reducedTaxRate3Label?: string | null;
  reverseChargeTaxRateLabel?: string | null;

  // Rental agreement settings
  defaultRentalAgreementDurationUnit?: DurationUnitEnum | null;
  minimumRentalAgreementDuration?: number | null;
  maximumRentalAgreementDuration?: number | null;

  availabilityThreshold?: number | null;

  /**
   * Controls how much to charge or pre-authorize when initializing a reservation payment.
   */
  reservationPaymentAmount?: ReservationPaymentAmountEnum | null;

  reservationMode?: ReservationCheckoutModeEnum | null;

  @Type(() => TenantSettingsReservationPercentageDepositRoundingDto)
  reservationPercentageDepositRounding?: TenantSettingsReservationPercentageDepositRoundingDto | null;

  distanceUnit?: DistanceUnitEnum | null;
  fuelConsumptionUnit?: FuelConsumptionUnitEnum | null;
  vehicleEmissionsUnitCO2?: VehicleEmissionsUnitCO2Enum | null;
  vehicleEngineOutputUnit?: VehicleEngineOutputUnitEnum | null;
  vehicleFuelTankCapacityUnit?: VehicleFuelTankCapacityUnitEnum | null;

  currencyIsoCode?: string | null;
  defaultLanguageId?: number | null;
  defaultCountryId?: number | null;

  logoUri?: string | null;

  @Type(() => TenantSettingsDiscountsDto)
  discounts?: TenantSettingsDiscountsDto | null;

  @Type(() => TenantSettingsMarketingDto)
  marketing?: TenantSettingsMarketingDto | null;

  @Type(() => TenantSettingsReportsDto)
  reports?: TenantSettingsReportsDto | null;

  @Type(() => TenantSettingsPublicIntegrationsDto)
  integrations?: TenantSettingsPublicIntegrationsDto | null;
}
